import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import axios from "axios";
import {
  FaFilter,
  FaTimes,
  FaBookmark,
  FaRegBookmark,
  FaClock,
  FaPlay,
  FaCheck,
  FaSpinner,
  FaSearch,
  FaStar,
  FaRegStar,
  FaChevronDown,
  FaChevronRight,
  FaChevronLeft,
  FaGlobe,
  FaThumbtack,
  FaRobot,
} from "react-icons/fa";

const Matches = () => {
  const [allMatches, setAllMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLeagues, setExpandedLeagues] = useState(() => {
    const saved = localStorage.getItem("expandedLeagues");
    return saved ? JSON.parse(saved) : {};
  });
  const [expandedCountries, setExpandedCountries] = useState({});
  const [leagueMatches, setLeagueMatches] = useState({});
  const [loadingLeagues, setLoadingLeagues] = useState({});
  const navigate = useNavigate();
  const [hideFinished, setHideFinished] = useState(false);
  const [pinnedLeagues, setPinnedLeagues] = useState(() => {
    const saved = localStorage.getItem("pinnedLeagues");
    return saved ? JSON.parse(saved) : {};
  });
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [favoriteMatches, setFavoriteMatches] = useState(() => {
    const saved = localStorage.getItem("favoriteMatches");
    return saved ? JSON.parse(saved) : [];
  });
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);

  // Inițializează selectedDate din sessionStorage dacă există
  const [selectedDate, setSelectedDate] = useState(() => {
    const savedDate = sessionStorage.getItem("matchesSelectedDate");
    if (savedDate) {
      const parsed = new Date(savedDate);
      if (!isNaN(parsed.getTime())) {
        parsed.setHours(0, 0, 0, 0);
        return parsed;
      }
    }
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });

  const [aiEligibleMatches, setAiEligibleMatches] = useState(() => {
    const saved = localStorage.getItem("aiEligibleMatches");
    return saved ? JSON.parse(saved) : {};
  });
  const [checkingEligible, setCheckingEligible] = useState(false);

  // Salvează ligile favorite în localStorage
  useEffect(() => {
    localStorage.setItem("pinnedLeagues", JSON.stringify(pinnedLeagues));
  }, [pinnedLeagues]);

  useEffect(() => {
    localStorage.setItem("favoriteMatches", JSON.stringify(favoriteMatches));
  }, [favoriteMatches]);

  useEffect(() => {
    localStorage.setItem("expandedLeagues", JSON.stringify(expandedLeagues));
  }, [expandedLeagues]);

  useEffect(() => {
    localStorage.setItem(
      "aiEligibleMatches",
      JSON.stringify(aiEligibleMatches),
    );
  }, [aiEligibleMatches]);

  // Salvează data în sessionStorage când se schimbă
  useEffect(() => {
    sessionStorage.setItem("matchesSelectedDate", selectedDate.toISOString());
  }, [selectedDate]);

  // La încărcarea paginii, restaurează scroll-ul
  useEffect(() => {
    const savedPosition = sessionStorage.getItem("matchesScrollPosition");
    if (savedPosition && !loading) {
      requestAnimationFrame(() => {
        window.scrollTo({
          top: parseInt(savedPosition),
          behavior: "auto",
        });
      });
    }
  }, [loading]);

  // Salvează poziția de scroll înainte de a părăsi pagina
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem(
        "matchesScrollPosition",
        window.scrollY.toString(),
      );
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Reset hideFinished when activeFilter changes away from "all"
  useEffect(() => {
    if (activeFilter !== "all" && hideFinished) {
      setHideFinished(false);
    }
  }, [activeFilter]);

  // Date navigation
  const prevDay = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - 1);
    setSelectedDate(d);
  };

  const nextDay = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 1);
    setSelectedDate(d);
  };

  const toggleFavoriteMatch = (matchId) => {
    setFavoriteMatches((prev) => {
      if (prev.includes(matchId)) {
        return prev.filter((id) => id !== matchId);
      } else {
        return [...prev, matchId];
      }
    });
  };

  const togglePinLeague = (leagueId) => {
    setPinnedLeagues((prev) => ({
      ...prev,
      [leagueId]: !prev[leagueId],
    }));
  };

  const toggleLeague = async (leagueId, leagueData) => {
    setExpandedLeagues((prev) => {
      const newState = !prev[leagueId];
      if (newState && !leagueMatches[leagueId]) {
        fetchLeagueMatches(leagueId, leagueData);
      }
      return {
        ...prev,
        [leagueId]: newState,
      };
    });
  };

  const toggleCountry = (countryName) => {
    setExpandedCountries((prev) => ({
      ...prev,
      [countryName]: !prev[countryName],
    }));
  };

  const fetchLeagueMatches = async (leagueId, leagueData) => {
    if (loadingLeagues[leagueId]) return;

    setLoadingLeagues((prev) => ({ ...prev, [leagueId]: true }));

    try {
      const selectedDayStart = new Date(selectedDate);
      selectedDayStart.setHours(0, 0, 0, 0);
      const selectedDayEnd = new Date(selectedDate);
      selectedDayEnd.setHours(23, 59, 59, 999);

      const leagueMatchesList = allMatches.filter((match) => {
        const matchLeagueId = match.leagueId || match.league_id;
        if (matchLeagueId !== leagueId) return false;
        if (!match.date) return false;
        try {
          const matchDate = new Date(match.date);
          return matchDate >= selectedDayStart && matchDate <= selectedDayEnd;
        } catch {
          return false;
        }
      });

      setLeagueMatches((prev) => ({
        ...prev,
        [leagueId]: leagueMatchesList,
      }));
    } catch (error) {
      console.error(`Error fetching matches for league ${leagueId}:`, error);
      setLeagueMatches((prev) => ({
        ...prev,
        [leagueId]: [],
      }));
    } finally {
      setLoadingLeagues((prev) => ({ ...prev, [leagueId]: false }));
    }
  };

  const fetchEligibleAIMatches = async () => {
    try {
      setCheckingEligible(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/predictions/eligible-matches`,
        {
          headers: {
            "Content-Type": "application/json",
            key: process.env.REACT_APP_ACCESKEY || "test-key",
          },
        },
      );

      if (response.data && response.data.success && response.data.matches) {
        const eligibleMap = {};
        response.data.matches.forEach((matchId) => {
          eligibleMap[matchId] = true;
        });
        setAiEligibleMatches(eligibleMap);
        localStorage.setItem("aiEligibleMatches", JSON.stringify(eligibleMap));
        console.log(
          `✅ Loaded ${response.data.matches.length} eligible matches for AI Analysis`,
        );
      }
    } catch (error) {
      console.error("Error fetching eligible AI matches:", error);
    } finally {
      setCheckingEligible(false);
    }
  };

  useEffect(() => {
    if (!loading && allMatches.length > 0) {
      Object.keys(expandedLeagues).forEach((leagueId) => {
        if (expandedLeagues[leagueId] && !leagueMatches[leagueId]) {
          const league = allLeagues.find((l) => l.leagueId === leagueId);
          if (league) {
            fetchLeagueMatches(leagueId, league);
          }
        }
      });
    }
  }, [loading, allMatches]);

  useEffect(() => {
    if (!loading && allMatches.length > 0 && !checkingEligible) {
      fetchEligibleAIMatches();
    }
  }, [loading, allMatches]);

  useEffect(() => {
    Object.keys(expandedLeagues).forEach((leagueId) => {
      if (expandedLeagues[leagueId]) {
        const league = allLeagues.find((l) => l.leagueId === leagueId);
        if (league) {
          fetchLeagueMatches(leagueId, league);
        }
      }
    });
  }, [selectedDate]);

  const getTimeInfo = (dateString) => {
    if (!dateString) return { localTime: "-" };
    try {
      const date = new Date(dateString);
      return {
        localTime: date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }),
      };
    } catch {
      return { localTime: "-" };
    }
  };

  const getMatchStatus = (match) => {
    if (!match.date) {
      return {
        type: "upcoming",
        label: "TBD",
        badgeColor: "bg-gray-700",
        icon: <FaClock className="inline mr-1" />,
      };
    }

    try {
      const now = new Date();
      const matchDate = new Date(match.date);
      const diff = matchDate.getTime() - now.getTime();
      const minutes = Math.floor(diff / (1000 * 60));

      if (
        match.status === "FT" ||
        match.status === "AET" ||
        match.status === "PEN"
      ) {
        return {
          type: "finished",
          label: "Finished",
          badgeColor: "bg-red-900/60",
          icon: <FaCheck className="inline mr-1" />,
        };
      }

      if (minutes > 0) {
        if (minutes < 60) {
          return {
            type: "upcoming",
            label: `In ${minutes}m`,
            badgeColor: "bg-blue-700",
            icon: <FaClock className="inline mr-1" />,
          };
        }
        const hours = Math.floor(minutes / 60);
        return {
          type: "upcoming",
          label: `In ${hours}h`,
          badgeColor: "bg-blue-700",
          icon: <FaClock className="inline mr-1" />,
        };
      }

      if (minutes >= -115 && minutes <= 0) {
        return {
          type: "live",
          label: "LIVE",
          badgeColor: "bg-green-900/60 animate-pulse",
          icon: <FaPlay className="inline mr-1" />,
        };
      }

      return {
        type: "finished",
        label: "Finished",
        badgeColor: "bg-red-900/60",
        icon: <FaCheck className="inline mr-1" />,
      };
    } catch {
      return {
        type: "upcoming",
        label: "TBD",
        badgeColor: "bg-gray-700",
        icon: <FaClock className="inline mr-1" />,
      };
    }
  };

  const fetchMatches = async () => {
    setLoading(true);
    try {
      console.log("Fetching matches...");
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/matches/getmatches`,
        {
          headers: { key: process.env.REACT_APP_ACCESKEY || "test-key" },
          timeout: 10000,
        },
      );

      console.log("Matches response:", res.data);
      setAllMatches(res.data.matches || []);
    } catch (err) {
      console.error("Fetch error:", err);
      setAllMatches([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatches();
  }, []);

  const getTodayMatches = useMemo(() => {
    const today = new Date(selectedDate);
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return allMatches.filter((match) => {
      if (!match.date) return false;
      try {
        const matchDate = new Date(match.date);
        return matchDate >= today && matchDate < tomorrow;
      } catch {
        return false;
      }
    });
  }, [allMatches, selectedDate]);

  const sidebarData = useMemo(() => {
    const todayMatches = getTodayMatches;
    const pinnedLeaguesToday = [];
    const countriesMap = new Map();

    todayMatches.forEach((match) => {
      const leagueId = match.leagueId || match.league_id || "unknown";
      const leagueName = match.league || match.league_name || "Unknown League";
      const country = match.country || "Unknown Country";
      const logo = match.logo || match.league_logo || null;
      const isPinned = pinnedLeagues[leagueId] || false;

      if (isPinned) {
        const existingPinned = pinnedLeaguesToday.find(
          (l) => l.leagueId === leagueId,
        );
        if (!existingPinned) {
          pinnedLeaguesToday.push({
            leagueId,
            leagueName,
            country,
            logo,
            matches: [],
          });
        }
        const pinnedLeague = pinnedLeaguesToday.find(
          (l) => l.leagueId === leagueId,
        );
        pinnedLeague.matches.push(match);
      }

      if (!countriesMap.has(country)) {
        countriesMap.set(country, new Map());
      }

      const countryLeagues = countriesMap.get(country);
      if (!countryLeagues.has(leagueId)) {
        countryLeagues.set(leagueId, {
          leagueId,
          leagueName,
          country,
          logo,
          isPinned,
          matches: [],
        });
      }

      countryLeagues.get(leagueId).matches.push(match);
    });

    const countries = Array.from(countriesMap.entries()).map(
      ([countryName, leagues]) => ({
        country: countryName,
        leagues: Array.from(leagues.values()),
      }),
    );

    countries.sort((a, b) => a.country.localeCompare(b.country));

    countries.forEach((country) => {
      country.leagues.sort((a, b) => a.leagueName.localeCompare(b.leagueName));
    });

    return { pinnedLeaguesToday, countries };
  }, [getTodayMatches, pinnedLeagues]);

  const allLeagues = useMemo(() => {
    const leaguesMap = new Map();

    allMatches.forEach((match) => {
      const leagueId = match.leagueId || match.league_id || "unknown";
      if (!leaguesMap.has(leagueId)) {
        leaguesMap.set(leagueId, {
          leagueId,
          leagueName: match.league || match.league_name || "Unknown League",
          leagueLogo: match.logo || match.league_logo || null,
          country: match.country || "Unknown Country",
        });
      }
    });

    return Array.from(leaguesMap.values());
  }, [allMatches]);

  const getFilteredLeagues = useMemo(() => {
    const selectedDayStart = new Date(selectedDate);
    selectedDayStart.setHours(0, 0, 0, 0);
    const selectedDayEnd = new Date(selectedDate);
    selectedDayEnd.setHours(23, 59, 59, 999);

    const leaguesMap = new Map();

    allMatches.forEach((match) => {
      if (!match.date) return;
      try {
        const matchDate = new Date(match.date);
        if (matchDate < selectedDayStart || matchDate > selectedDayEnd) return;
      } catch (e) {
        return;
      }

      const status = getMatchStatus(match);

      if (activeFilter === "live" && status.type !== "live") return;
      if (activeFilter === "finished" && status.type !== "finished") return;
      if (hideFinished && status.type === "finished") return;

      if (
        activeFilter === "3h" ||
        activeFilter === "6h" ||
        activeFilter === "12h"
      ) {
        const hoursMap = { "3h": 3, "6h": 6, "12h": 12 };
        const hours = hoursMap[activeFilter];
        try {
          const matchDate = new Date(match.date);
          const now = new Date();
          const futureLimit = new Date(now.getTime() + hours * 60 * 60 * 1000);
          if (matchDate > futureLimit || matchDate < now) return;
        } catch (e) {
          return;
        }
      }

      if (searchQuery) {
        const query = searchQuery.toLowerCase().trim();
        const words = searchQuery.toLowerCase().trim().split(/\s+/);

        const home = (match.homeTeam || "").toLowerCase();
        const away = (match.awayTeam || "").toLowerCase();
        const leagueName = (match.league || match.league_name || "").toLowerCase();
        const country = (match.country || "").toLowerCase();

        const matchesSearch = words.every((word) =>
          home.includes(word) ||
          away.includes(word) ||
          leagueName.includes(word) ||
          country.includes(word)
        );

        if (!matchesSearch) return;
      }

      const leagueId = match.leagueId || match.league_id || "unknown";
      const leagueName = match.league || match.league_name || "Unknown League";
      const logo = match.logo || match.league_logo || null;
      const country = match.country || "Unknown Country";
      const isPinned = pinnedLeagues[leagueId] || false;

      if (!leaguesMap.has(leagueId)) {
        leaguesMap.set(leagueId, {
          leagueId,
          leagueName,
          leagueLogo: logo,
          country,
          isPinned,
          matchCount: 0,
          matches: [],
        });
      }

      const league = leaguesMap.get(leagueId);
      league.matchCount++;
      league.matches.push(match);
    });

    let leagues = Array.from(leaguesMap.values());

    if (showOnlyFavorites) {
      leagues = leagues
        .map((league) => ({
          ...league,
          matches: league.matches.filter((match) => {
            const matchId = match.id || match.fixture_id;
            return favoriteMatches.includes(matchId);
          }),
        }))
        .filter((league) => league.matches.length > 0);
    }

    leagues.sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return a.leagueName.localeCompare(b.leagueName);
    });

    return leagues;
  }, [
    allMatches,
    selectedDate,
    pinnedLeagues,
    activeFilter,
    hideFinished,
    showOnlyFavorites,
    favoriteMatches,
    searchQuery,
  ]);

  const getLeagueMatchesWithFilters = useCallback(
    (leagueId) => {
      const league = getFilteredLeagues.find((l) => l.leagueId === leagueId);
      return league ? league.matches : [];
    },
    [getFilteredLeagues],
  );

  const getMatchResult = (match) => {
    const apiStatus = match.match_status || match.status;
    const isFinished =
      apiStatus && ["FT", "AET", "PEN", "FT_PEN"].includes(apiStatus);

    if (!isFinished) {
      return {
        homeResult: null,
        awayResult: null,
        hasResult: false,
      };
    }

    const homeResult =
      match.goals_home !== undefined
        ? match.goals_home
        : match.home_result !== undefined
          ? match.home_result
          : match.homeGoals !== undefined
            ? match.homeGoals
            : null;

    const awayResult =
      match.goals_away !== undefined
        ? match.goals_away
        : match.away_result !== undefined
          ? match.away_result
          : match.awayGoals !== undefined
            ? match.awayGoals
            : null;

    const hasResult = homeResult !== null && awayResult !== null;

    return {
      homeResult: hasResult ? homeResult : null,
      awayResult: hasResult ? awayResult : null,
      hasResult,
    };
  };

  const clearFilters = () => {
    setActiveFilter("all");
    setHideFinished(false);
    setSearchQuery("");
    setShowOnlyFavorites(false);
  };

  const handleViewDetails = (id) => {
    if (!id) {
      console.error("No ID provided for match details");
      return;
    }
    sessionStorage.setItem("matchesScrollPosition", window.scrollY.toString());
    console.log("Navigating to match:", id);
    navigate(`/match/${id}`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
          <div className="text-center">
            <FaSpinner className="inline-block animate-spin text-4xl text-green-400 mb-4" />
            <p className="text-gray-300">Loading matches...</p>
            <p className="text-gray-500 text-sm mt-2">Please wait</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-7xl mx-auto p-4 md:p-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <span className="text-white drop-shadow-lg">⚽</span>
                <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                  Football Fixtures
                </span>
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                {selectedDate.toLocaleDateString(undefined, {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>

            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 w-full md:w-auto">
              <div className="relative w-full md:w-64">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search teams, leagues..."
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-800/80 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <FaTimes className="text-gray-400 hover:text-white" />
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2 bg-gray-800/80 border border-gray-700 px-3 py-1.5 rounded-xl">
                <button
                  onClick={prevDay}
                  className="p-2 text-green-400 hover:text-green-300 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <FaChevronLeft />
                </button>
                <div className="text-sm font-medium text-white px-3 py-1">
                  {selectedDate.toLocaleDateString(undefined, {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                  })}
                </div>
                <button
                  onClick={nextDay}
                  className="p-2 text-green-400 hover:text-green-300 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            <div
              className="lg:w-80 space-y-4 lg:sticky lg:top-24 overflow-y-auto scrollbar-thin scrollbar-track-gray-800/30 scrollbar-thumb-green-500 hover:scrollbar-thumb-green-400"
              style={{ height: "calc(100vh - 6rem)" }}
            >
              <div className="bg-gray-800/60 backdrop-blur-sm border border-green-500/20 rounded-xl overflow-hidden">
                <div className="bg-gradient-to-r from-green-900/40 to-emerald-900/40 px-4 py-3 border-b border-green-500/20 sticky top-0 bg-gray-800/90 z-10 backdrop-blur-sm">
                  <h2 className="font-semibold text-white flex items-center gap-2">
                    <FaThumbtack className="text-green-400 text-sm rotate-45" />
                    Pinned Leagues
                  </h2>
                </div>
                <div className="p-3">
                  {sidebarData.pinnedLeaguesToday.length > 0 ? (
                    <div className="space-y-2">
                      {sidebarData.pinnedLeaguesToday.map((league) => (
                        <div
                          key={league.leagueId}
                          className="bg-gray-700/40 rounded-lg p-2 hover:bg-gray-700/60 transition-colors cursor-pointer"
                          onClick={() => {
                            const leagueElement = document.getElementById(
                              `league-${league.leagueId}`,
                            );
                            if (leagueElement) {
                              leagueElement.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                              });
                              if (!expandedLeagues[league.leagueId]) {
                                toggleLeague(league.leagueId, league);
                              }
                            }
                          }}
                        >
                          <div className="flex items-center gap-2">
                            {league.logo ? (
                              <img
                                src={league.logo}
                                alt={league.leagueName}
                                className="w-6 h-6 object-contain"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <div className="w-6 h-6 bg-gray-600 rounded-full flex items-center justify-center text-xs">
                                ⚽
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-white truncate">
                                {league.country}: {league.leagueName}
                              </p>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-xs bg-gray-600 px-2 py-0.5 rounded-full text-gray-300">
                                {league.matches.length}
                              </span>
                              <FaBookmark className="text-green-400 text-xs flex-shrink-0" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm text-center py-4">
                      No pinned leagues with matches
                    </p>
                  )}
                </div>
              </div>

              <div className="bg-gray-800/60 backdrop-blur-sm border border-green-500/20 rounded-xl overflow-hidden">
                <div className="bg-gradient-to-r from-blue-900/40 to-indigo-900/40 px-4 py-3 border-b border-green-500/20 sticky top-0 bg-gray-800/90 z-10 backdrop-blur-sm">
                  <h2 className="font-semibold text-white flex items-center gap-2">
                    <FaGlobe className="text-blue-400" />
                    Countries
                  </h2>
                </div>
                <div className="p-3">
                  {sidebarData.countries.length > 0 ? (
                    <div className="space-y-2">
                      {sidebarData.countries.map((country) => (
                        <div
                          key={country.country}
                          className="border border-green-500/20 rounded-lg overflow-hidden"
                        >
                          <button
                            onClick={() => toggleCountry(country.country)}
                            className="w-full flex items-center justify-between p-2 bg-gray-700/40 hover:bg-gray-700/60 transition-colors"
                          >
                            <span className="text-sm font-medium text-white">
                              {country.country}
                            </span>
                            <span className="flex items-center gap-2">
                              <span className="text-xs bg-gray-600 px-2 py-0.5 rounded-full text-gray-300">
                                {country.leagues.length}
                              </span>
                              {expandedCountries[country.country] ? (
                                <FaChevronDown className="text-gray-400 text-xs" />
                              ) : (
                                <FaChevronRight className="text-gray-400 text-xs" />
                              )}
                            </span>
                          </button>

                          {expandedCountries[country.country] && (
                            <div className="p-2 space-y-2 bg-gray-800/40">
                              {country.leagues.map((league) => (
                                <div
                                  key={league.leagueId}
                                  className="flex items-center justify-between group"
                                >
                                  <div
                                    className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
                                    onClick={() => {
                                      const leagueElement =
                                        document.getElementById(
                                          `league-${league.leagueId}`,
                                        );
                                      if (leagueElement) {
                                        leagueElement.scrollIntoView({
                                          behavior: "smooth",
                                          block: "center",
                                        });
                                        if (!expandedLeagues[league.leagueId]) {
                                          toggleLeague(league.leagueId, league);
                                        }
                                      }
                                    }}
                                  >
                                    {league.logo ? (
                                      <img
                                        src={league.logo}
                                        alt={league.leagueName}
                                        className="w-5 h-5 object-contain"
                                        onError={(e) => {
                                          e.target.style.display = "none";
                                        }}
                                      />
                                    ) : (
                                      <div className="w-5 h-5 bg-gray-600 rounded-full flex items-center justify-center text-[10px]">
                                        ⚽
                                      </div>
                                    )}
                                    <span className="text-xs text-gray-300 truncate">
                                      {league.leagueName}
                                    </span>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      togglePinLeague(league.leagueId);
                                    }}
                                    className={`p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity ${
                                      league.isPinned
                                        ? "text-green-400"
                                        : "text-gray-500 hover:text-gray-300"
                                    }`}
                                  >
                                    {league.isPinned ? (
                                      <FaBookmark className="text-xs" />
                                    ) : (
                                      <FaRegBookmark className="text-xs" />
                                    )}
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm text-center py-4">
                      No matches on this date
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Matches list */}
            <div className="flex-1">
              <div className="mb-6">
                <div className="flex flex-wrap items-center gap-2 mb-4 p-4 bg-gray-800/60 backdrop-blur-sm border border-green-500/20 rounded-xl">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setActiveFilter("all")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        activeFilter === "all"
                          ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg shadow-green-600/20"
                          : "bg-gray-700/60 text-gray-300 hover:bg-gray-700"
                      }`}
                    >
                      All
                    </button>
                    <button
                      onClick={() => setActiveFilter("live")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                        activeFilter === "live"
                          ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg shadow-green-600/20"
                          : "bg-gray-700/60 text-gray-300 hover:bg-gray-700"
                      }`}
                    >
                      <FaPlay className="animate-pulse text-xs" />
                      Live
                    </button>
                    <button
                      onClick={() => setActiveFilter("finished")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        activeFilter === "finished"
                          ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg shadow-green-600/20"
                          : "bg-gray-700/60 text-gray-300 hover:bg-gray-700"
                      }`}
                    >
                      Finished
                    </button>
                    <button
                      onClick={() => setActiveFilter("3h")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        activeFilter === "3h"
                          ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg shadow-green-600/20"
                          : "bg-gray-700/60 text-gray-300 hover:bg-gray-700"
                      }`}
                    >
                      3h
                    </button>
                    <button
                      onClick={() => setActiveFilter("6h")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        activeFilter === "6h"
                          ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg shadow-green-600/20"
                          : "bg-gray-700/60 text-gray-300 hover:bg-gray-700"
                      }`}
                    >
                      6h
                    </button>
                    <button
                      onClick={() => setActiveFilter("12h")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        activeFilter === "12h"
                          ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg shadow-green-600/20"
                          : "bg-gray-700/60 text-gray-300 hover:bg-gray-700"
                      }`}
                    >
                      12h
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-2 ml-auto">
                    {activeFilter === "all" && (
                      <label className="flex items-center gap-2 px-3 py-2 bg-gray-700/60 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
                        <input
                          type="checkbox"
                          checked={hideFinished}
                          onChange={(e) => setHideFinished(e.target.checked)}
                          className="text-green-500 rounded focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-300 whitespace-nowrap">
                          Hide Finished
                        </span>
                      </label>
                    )}

                    <label className="flex items-center gap-2 px-3 py-2 bg-gray-700/60 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors">
                      <input
                        type="checkbox"
                        checked={showOnlyFavorites}
                        onChange={(e) => setShowOnlyFavorites(e.target.checked)}
                        className="text-green-500 rounded focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-300 whitespace-nowrap">
                        Favorites
                      </span>
                    </label>
                  </div>

                  {(activeFilter !== "all" ||
                    hideFinished ||
                    searchQuery ||
                    showOnlyFavorites) && (
                    <button
                      onClick={clearFilters}
                      className="px-4 py-2 bg-gray-700/60 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-gray-700 transition-colors flex items-center gap-2"
                    >
                      <FaTimes /> Clear
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                {getFilteredLeagues.length > 0 ? (
                  getFilteredLeagues.map((league) => {
                    const expanded = expandedLeagues[league.leagueId] === true;
                    const leagueMatchesList = expanded
                      ? getLeagueMatchesWithFilters(league.leagueId)
                      : [];
                    const isLoading = loadingLeagues[league.leagueId];

                    return (
                      <div
                        key={league.leagueId}
                        id={`league-${league.leagueId}`}
                        className="bg-gray-800/60 backdrop-blur-sm border border-green-500/20 rounded-xl overflow-hidden"
                      >
                        <div
                          className={`px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-700/40 transition-colors ${
                            league.isPinned
                              ? "bg-gradient-to-r from-green-900/20 to-emerald-900/20"
                              : ""
                          }`}
                          onClick={() => toggleLeague(league.leagueId, league)}
                        >
                          <div className="flex items-center gap-3">
                            {league.leagueLogo ? (
                              <img
                                src={league.leagueLogo}
                                alt={league.leagueName}
                                className="w-8 h-8 object-contain"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                                ⚽
                              </div>
                            )}
                            <div>
                              <h3 className="font-semibold text-white">
                                {league.country}: {league.leagueName}
                              </h3>
                              <p className="text-xs text-gray-400">
                                {league.matchCount} matches
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <span className="text-sm bg-gray-700 px-2 py-1 rounded-lg text-gray-300">
                              {league.matchCount}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                togglePinLeague(league.leagueId);
                              }}
                              className={`p-2 rounded-lg hover:bg-gray-700 transition-colors ${
                                league.isPinned
                                  ? "text-green-400"
                                  : "text-gray-400 hover:text-green-400"
                              }`}
                              title={
                                league.isPinned ? "Unpin league" : "Pin league"
                              }
                            >
                              {league.isPinned ? (
                                <FaBookmark />
                              ) : (
                                <FaRegBookmark />
                              )}
                            </button>
                            <div className="p-2">
                              {expanded ? (
                                <FaChevronDown className="text-gray-400" />
                              ) : (
                                <FaChevronRight className="text-gray-400" />
                              )}
                            </div>
                          </div>
                        </div>

                        {expanded && (
                          <div className="border-t border-green-500/20">
                            {isLoading ? (
                              <div className="p-8 text-center">
                                <FaSpinner className="inline-block animate-spin text-2xl text-green-400 mb-2" />
                                <p className="text-gray-400">
                                  Loading matches...
                                </p>
                              </div>
                            ) : leagueMatchesList.length > 0 ? (
                              <div className="overflow-x-auto">
                                <table className="w-full hidden md:table">
                                  <thead className="bg-gray-700/30">
                                    <tr>
                                      <th className="py-3 px-4 text-left text-gray-300 font-medium text-sm w-1/6">
                                        Time
                                      </th>
                                      <th className="py-3 px-4 text-left text-gray-300 font-medium text-sm w-2/6">
                                        Match
                                      </th>
                                      <th className="py-3 px-4 text-center text-gray-300 font-medium text-sm w-1/6">
                                        Result
                                      </th>
                                      <th className="py-3 px-4 text-center text-gray-300 font-medium text-sm w-1/6">
                                        AI Analysis
                                      </th>
                                      <th className="py-3 px-4 text-center text-gray-300 font-medium text-sm w-1/6">
                                        Actions
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {leagueMatchesList.map((match) => {
                                      const id = match.id || match.fixture_id;
                                      const status = getMatchStatus(match);
                                      const time = getTimeInfo(match.date);
                                      const result = getMatchResult(match);
                                      const isFavorite =
                                        favoriteMatches.includes(id);
                                      const hasAIAvailable =
                                        aiEligibleMatches[id];

                                      return (
                                        <tr
                                          key={id}
                                          className="border-t border-green-500/10 hover:bg-gray-700/30 transition-colors"
                                        >
                                          <td className="py-4 px-4 align-middle">
                                            <div className="flex items-center gap-1 text-white mb-1">
                                              <FaClock
                                                className="text-gray-400"
                                                size={12}
                                              />
                                              <span className="font-semibold">
                                                {time.localTime}
                                              </span>
                                            </div>
                                            <div
                                              className={`text-xs px-2 py-1 rounded-full inline-flex items-center ${status.badgeColor} text-white`}
                                            >
                                              {status.icon}
                                              {status.label}
                                            </div>
                                          </td>
                                          <td className="py-4 px-4 align-middle">
                                            <div className="relative min-h-[72px] flex items-center">
                                              <div className="absolute left-0 top-1/2 -translate-y-1/2">
                                                <button
                                                  onClick={() =>
                                                    toggleFavoriteMatch(id)
                                                  }
                                                  className={`transition-colors ${
                                                    isFavorite
                                                      ? "text-yellow-400"
                                                      : "text-gray-500 hover:text-yellow-400"
                                                  }`}
                                                >
                                                  {isFavorite ? (
                                                    <FaStar size={16} />
                                                  ) : (
                                                    <FaRegStar size={16} />
                                                  )}
                                                </button>
                                              </div>

                                              <div className="pl-10 w-full">
                                                <div className="space-y-2">
                                                  <div className="flex items-center gap-3">
                                                    {match.homeLogo ? (
                                                      <img
                                                        src={match.homeLogo}
                                                        alt={match.homeTeam}
                                                        className="w-6 h-6 object-contain"
                                                        onError={(e) =>
                                                          (e.target.style.display =
                                                            "none")
                                                        }
                                                      />
                                                    ) : (
                                                      <div className="w-6 h-6" />
                                                    )}
                                                    <span className="text-white text-sm truncate">
                                                      {match.homeTeam ||
                                                        "Unknown"}
                                                    </span>
                                                  </div>
                                                  <div className="flex items-center gap-3">
                                                    {match.awayLogo ? (
                                                      <img
                                                        src={match.awayLogo}
                                                        alt={match.awayTeam}
                                                        className="w-6 h-6 object-contain"
                                                        onError={(e) =>
                                                          (e.target.style.display =
                                                            "none")
                                                        }
                                                      />
                                                    ) : (
                                                      <div className="w-6 h-6" />
                                                    )}
                                                    <span className="text-white text-sm truncate">
                                                      {match.awayTeam ||
                                                        "Unknown"}
                                                    </span>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          </td>
                                          <td className="py-4 px-4 text-center align-middle">
                                            {result.hasResult ? (
                                              <div className="space-y-1">
                                                <div className="font-bold text-lg text-white">
                                                  {result.homeResult}
                                                </div>
                                                <div className="font-bold text-lg text-white">
                                                  {result.awayResult}
                                                </div>
                                              </div>
                                            ) : (
                                              <div className="text-gray-500 text-lg">
                                                -
                                              </div>
                                            )}
                                          </td>
                                          <td className="py-4 px-4 text-center align-middle">
                                            {hasAIAvailable ? (
                                              <div className="flex items-center justify-center">
                                                <div className="relative group">
                                                  <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center animate-pulse shadow-lg shadow-purple-500/50">
                                                    <FaRobot className="w-5 h-5 text-white" />
                                                  </div>
                                                  <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 hidden group-hover:block bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                                                    AI Analysis Available
                                                  </div>
                                                </div>
                                              </div>
                                            ) : (
                                              <div className="text-gray-500 text-sm">
                                                -
                                              </div>
                                            )}
                                          </td>
                                          <td className="py-4 px-4 text-center align-middle">
                                            <button
                                              onClick={() =>
                                                handleViewDetails(id)
                                              }
                                              className="px-4 py-2 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white text-sm hover:from-green-500 hover:to-emerald-500 transition-all shadow-lg shadow-green-600/20"
                                            >
                                              Details
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>

                                <div className="md:hidden">
                                  {leagueMatchesList.map((match) => {
                                    const id = match.id || match.fixture_id;
                                    const status = getMatchStatus(match);
                                    const time = getTimeInfo(match.date);
                                    const result = getMatchResult(match);
                                    const isFavorite =
                                      favoriteMatches.includes(id);
                                    const hasAIAvailable =
                                      aiEligibleMatches[id];

                                    return (
                                      <div
                                        key={id}
                                        className="border-t border-green-500/10 p-4 hover:bg-gray-700/30"
                                      >
                                        <div className="flex justify-between items-center mb-3">
                                          <div
                                            className={`text-xs px-2 py-1 rounded-full inline-flex items-center ${status.badgeColor} text-white`}
                                          >
                                            {status.icon}
                                            {status.label}
                                          </div>
                                          <div className="flex items-center gap-1 text-sm text-gray-400">
                                            <FaClock size={12} />
                                            <span>{time.localTime}</span>
                                          </div>
                                        </div>

                                        <div className="relative min-h-[60px]">
                                          <div className="absolute left-0 top-1/2 -translate-y-1/2">
                                            <button
                                              onClick={() =>
                                                toggleFavoriteMatch(id)
                                              }
                                              className={`transition-colors ${
                                                isFavorite
                                                  ? "text-yellow-400"
                                                  : "text-gray-500 hover:text-yellow-400"
                                              }`}
                                            >
                                              {isFavorite ? (
                                                <FaStar size={16} />
                                              ) : (
                                                <FaRegStar size={16} />
                                              )}
                                            </button>
                                          </div>

                                          <div className="pl-10">
                                            <div className="space-y-2">
                                              <div className="flex justify-between items-center">
                                                <div className="flex items-center gap-2 flex-1">
                                                  {match.homeLogo ? (
                                                    <img
                                                      src={match.homeLogo}
                                                      alt={match.homeTeam}
                                                      className="w-5 h-5 object-contain"
                                                      onError={(e) =>
                                                        (e.target.style.display =
                                                          "none")
                                                      }
                                                    />
                                                  ) : null}
                                                  <span className="text-sm font-medium text-white truncate">
                                                    {match.homeTeam ||
                                                      "Unknown"}
                                                  </span>
                                                </div>
                                                <div className="text-base font-bold text-white min-w-[30px] text-right">
                                                  {result.hasResult
                                                    ? result.homeResult
                                                    : "-"}
                                                </div>
                                              </div>
                                              <div className="flex justify-between items-center">
                                                <div className="flex items-center gap-2 flex-1">
                                                  {match.awayLogo ? (
                                                    <img
                                                      src={match.awayLogo}
                                                      alt={match.awayTeam}
                                                      className="w-5 h-5 object-contain"
                                                      onError={(e) =>
                                                        (e.target.style.display =
                                                          "none")
                                                      }
                                                    />
                                                  ) : null}
                                                  <span className="text-sm font-medium text-white truncate">
                                                    {match.awayTeam ||
                                                      "Unknown"}
                                                  </span>
                                                </div>
                                                <div className="text-base font-bold text-white min-w-[30px] text-right">
                                                  {result.hasResult
                                                    ? result.awayResult
                                                    : "-"}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>

                                        <div className="mt-3 flex items-center gap-2">
                                          {hasAIAvailable && (
                                            <div className="flex-shrink-0">
                                              <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center shadow-lg shadow-purple-500/50">
                                                <FaRobot className="w-4 h-4 text-white" />
                                              </div>
                                            </div>
                                          )}
                                          <button
                                            onClick={() =>
                                              handleViewDetails(id)
                                            }
                                            className={`flex-1 px-3 py-2 text-sm rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-500 hover:to-emerald-500 transition-all ${
                                              hasAIAvailable ? "ml-0" : ""
                                            }`}
                                          >
                                            View Details
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ) : (
                              <div className="p-8 text-center">
                                <p className="text-gray-400">
                                  No matches match your filters
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="bg-gray-800/60 backdrop-blur-sm p-8 rounded-xl border border-green-500/20 text-center">
                    <div className="text-4xl mb-4">⚽</div>
                    <p className="text-gray-300 text-lg">No leagues found</p>
                    <p className="text-gray-500 text-sm mt-2">
                      Try adjusting your filters or selecting a different date
                    </p>
                    <button
                      onClick={clearFilters}
                      className="mt-4 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors mr-2"
                    >
                      Clear filters
                    </button>
                    <button
                      onClick={fetchMatches}
                      className="mt-4 px-4 py-2 bg-green-800/60 text-green-300 rounded-lg hover:bg-green-700/60 transition-colors"
                    >
                      Refresh data
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(31, 41, 55, 0.5);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(75, 85, 99, 0.8);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(107, 114, 128, 0.8);
        }
      `}</style>
    </Layout>
  );
};

export default Matches;
